import isEmpty from 'lodash/isEmpty';

class ApiFilter {

    constructor(filterData = null) {
        this._andOr = 'and';
        this._count = {};
        this.clearParams();
        this.clearRelations();
        this.clearFilters();
        this.clearScopes();

        if (filterData !== null && typeof filterData === "object") {
            if (filterData.hasOwnProperty('andOr')) {
                this.andOr = filterData.andOr;
            }

            if (filterData.hasOwnProperty('count')) {
                this.count = filterData.count
            }

            if (filterData.hasOwnProperty('params')) {
                this.params = filterData.params;
            }
            if (filterData.hasOwnProperty('relations')) {
                this.relations = filterData.relations;
            }
            if (filterData.hasOwnProperty('filters')) {
                this.filters = filterData.filters;
            }
            if (filterData.hasOwnProperty('scopes')) {
                this.scopes = filterData.scopes;
            }
        }
    }

    get isValid() {
        return !isEmpty(this.params) ||
            !isEmpty(this.relations) ||
            !isEmpty(this.filters) ||
            !isEmpty(this.scopes);
    }

    /**
     * @returns {string}
     */
    get andOr() {
        return this._andOr;
    }

    /**
     * @param value
     */
    set andOr(value) {
        const valids = ['and', 'or'];
        if (valids.indexOf(value) < 0) {
            value = 'and';
        }
        this._andOr = value;
    }

    get count () {
        return this._count
    }

    set count (value) {
        for (const relation in value) {
            const condition = value[relation]

            if (typeof condition === 'object') {
                this._count[relation] = {
                    operator: condition.operator,
                    value: condition.value,
                    ...(condition.andOr ? {andOr: condition.andOr} : {}),
                    ...(condition.params ? {params: condition.params} : {})
                }
            } else if (typeof condition === 'string') {
                this._count[relation] = {
                    operator: '=',
                    value: condition
                }
            }
        }

        return this._count
    }

    /**
     * @returns {{}|*}
     */
    get params() {
        return this._params;
    }

    /**
     * @param paramValues
     */
    set params(paramValues) {
        this.clearParams();
        if (typeof paramValues !== 'object') {
            return;
        }
        let paramValue;
        for (let paramName in paramValues) {
            if (!paramValues.hasOwnProperty(paramName)) {
                continue;
            }
            paramValue = paramValues[paramName];
            this.addParam(paramName, paramValue);
        }
    }

    /**
     * @returns {{}|*}
     */
    get relations() {
        return this._relations;
    }

    /**
     * @param relationValues
     */
    set relations(relationValues) {
        this.clearRelations();
        if (typeof relationValues !== 'object') {
            return;
        }
        let relationValue;
        for (let relationName in relationValues) {
            if (!relationValues.hasOwnProperty(relationName)) {
                continue;
            }
            relationValue = relationValues[relationName];
            this.addRelation(relationName, relationValue);
        }
    }

    /**
     * @returns {[]|Array}
     */
    get filters() {
        return this._filters;
    }

    /**
     * @param apiFilters
     */
    set filters(apiFilters) {
        this.clearFilters();
        if (!Array.isArray(apiFilters)) {
            return;
        }
        let relationValue;
        for (let i = 0; i < apiFilters.length; i++) {
            this.addFilter(apiFilters[i]);
        }
    }

    /**
     * @returns {[]|Array}
     */
    get scopes() {
        return this._scopes;
    }

    /**
     * @param scopeValues
     */
    set scopes(scopeValues) {
        if (!Array.isArray(scopeValues)) {
            return;
        }
        for (let scopeName of scopeValues) {
            if (typeof scopeName !== 'string') {
                continue;
            }
            this.addScope(scopeName);
        }
    }

    /**
     *
     * @returns {boolean}
     */
    hasData() {
        return !isEmpty(this.params) ||
            !isEmpty(this.relations) ||
            !isEmpty(this.filters) ||
            !isEmpty(this.scopes);
    }

    /**
     * @returns {{andOr: string, params: {}|*, relations: {}|*}}
     */
    toObject() {
        const data = {
            'andOr': this.andOr,
        };

        if (!isEmpty(this.params)) {
            data['params'] = this.params;
        }

        if (!isEmpty(this.count)) {
            data['count'] = this.count
        }

        if (!isEmpty(this.relations)) {
            let relationFilter;
            data['relations'] = {};
            for (let relation in this.relations) {
                if (!this.relations.hasOwnProperty(relation)) {
                    continue;
                }
                data['relations'][relation] = this.relations[relation].toObject();
            }
        }

        if (!isEmpty(this.scopes)) {
            data['scopes'] = [];
            for (let scope of this.scopes) {
                if (typeof scope !== 'string') {
                    continue;
                }
                data['scopes'].push(scope);
            }
        }

        if (!isEmpty(this.filters)) {
            let relationFilter;
            data['filters'] = [];
            for (let i = 0; i < this.filters.length; i++) {
                data['filters'].push(this.filters[i].toObject());
            }
        }

        return data;
    }

    /**
     * @returns {string}
     */
    toJson() {
        return JSON.stringify(this.toObject());
    }

    /**
     * @returns {ApiFilter}
     */
    clearParams() {
        this._params = {};
        return this;
    }

    /**
     * @param paramName
     * @param paramValue
     * @returns {ApiFilter}
     */
    addParam(paramName, paramValue) {
        if (
            paramName === undefined ||
            paramValue === undefined
        ) {
            return this;
        }
        this._params[paramName] = paramValue;
        return this;
    }

    /**
     * @param paramName
     * @returns {ApiFilter}
     */
    forgetParam(paramName) {
        if (paramName === undefined) {
            return this;
        }
        delete this._params[paramName];
        return this;
    }

    /**
     * @returns {ApiFilter}
     */
    clearRelations() {
        this._relations = {};
        return this;
    }

    /**
     * @param relationName
     * @param apiFilter
     * @returns {ApiFilter}
     */
    addRelation(relationName, apiFilter) {
        if (
            relationName === undefined ||
            apiFilter === undefined ||
            !(apiFilter instanceof ApiFilter)
        ) {
            return this;
        }
        this._relations[relationName] = apiFilter;
        return this;
    }

    /**
     * @param scopeName
     * @param apiFilter
     * @returns {ApiFilter}
     */
    addScope(scope) {
        this._scopes.push(scope);
        return this;
    }

    /**
     * @returns {ApiFilter}
     */
    clearScopes() {
        this._scopes = [];
        return this;
    }

    /**
     * @param relationName
     * @returns {ApiFilter}
     */
    forgetRelation(relationName) {
        if (relationName === undefined) {
            return this;
        }
        delete this._relations[relationName];
        return this;
    }

    /**
     * @returns {ApiFilter}
     */
    clearFilters() {
        this._filters = [];
        return this;
    }

    /**
     * @param apiFilter
     * @returns {ApiFilter}
     */
    addFilter(apiFilter) {
        if (
            apiFilter === undefined ||
            !(apiFilter instanceof ApiFilter)
        ) {
            return this;
        }
        this._filters.push(apiFilter);
        return this;
    }
}

export default ApiFilter;
